import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useLoginDemoUserMutation, useLoginMutation } from '@/store/api/authApi';
import { useGetContactSupportQuery } from '@/store/api/userApi';
import { useAppDispatch } from '@/hooks/redux';
import { login } from '@/store/slices/authSlice';
import { Icons } from '@/utils/Icons';
import { getApiErrorMessage, getDeviceType, isGuestUser, showToast } from '@/utils';
import { loginSchema } from '@/utils/validation';
import OTPModal from '@/components/OTPModal';
import ForgotPasswordModal from '@/components/ForgotPasswordModal';
import { BRAND } from '@/config/brand';

type LoginFormData = z.infer<typeof loginSchema>;

type Props = {
  /** Called once the user is fully logged in (no OTP or password change still pending). */
  onDone: () => void;
  /** Switch to the signup form. */
  onSignup: () => void;
};

/** Login form, shown in the login popup (`AuthModal`). */
function LoginForm({ onDone, onSignup }: Props) {
  const [loginMutation, { isLoading, isError, error }] = useLoginMutation();
  const [demoLoginMutation, { isLoading: demoLoading }] = useLoginDemoUserMutation();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [verification, setVerification] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationData, setverificationData] = useState<{ username: string; type: string }>({
    username: '',
    type: '',
  });

  const dispatch = useAppDispatch();
  const isGuest = isGuestUser();
  useGetContactSupportQuery(undefined, { skip: isGuest });

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // ✅ Submit Handler
  const onSubmit = async (data: LoginFormData) => {
    try {
      const device = getDeviceType();
      const result = await loginMutation({
        username: data.username.trim(),
        password: data.password.trim(),
        device: device,
      }).unwrap();

      if (result?.accessToken) {
        if (result?.role?.name?.toLowerCase() !== 'user') {
          showToast('User does not exist', 'error');
          return;
        }
        dispatch(
          login({
            type: result.type,
            accessToken: result.accessToken,
          })
        );
      }

      if (result?.isTelegramEnabled || result?.mfaRequired) {
        setverificationData({
          username: data.username,
          type: result?.isTelegramEnabled ? 'telegram' : 'number',
        });
        setVerification(true);
      } else {
        // Includes a first login (passwordChanged false): the Header then shows the forced
        // Change Password popup once the user is loaded.
        onDone();
      }
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Login failed, please try again'), 'error');
    }
  };

  const handleDemoLogin = async () => {
    try {
      const result = await demoLoginMutation().unwrap();

      dispatch(login(result));
      onDone();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Demo login failed, please try again'), 'error');
    }
  };

  const inputWrap =
    'flex h-12 items-center gap-3 rounded-lg border border-shell-border bg-shell-raised px-4 transition focus-within:border-gold';
  const inputClass =
    'autofill-dark h-full min-w-0 flex-1 bg-transparent text-sm text-shell-fg outline-none placeholder:text-shell-muted';

  return (
    <div>
      <div>
        <h2 className="text-2xl font-bold text-shell-fg">Welcome back</h2>
        <p className="mt-1 text-sm text-shell-muted">Log in to continue to your account.</p>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <div>
            <label
              htmlFor="login-username"
              className="mb-1.5 block text-xs font-medium text-shell-muted"
            >
              Username / Mobile number
            </label>
            <div className={inputWrap}>
              <Icons.User className="shrink-0 text-shell-muted" />
              <input
                id="login-username"
                {...register('username')}
                onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                placeholder="Enter username or mobile"
                autoComplete="username"
                className={inputClass}
                type="text"
              />
            </div>
            {errors.username && (
              <p className="mt-1 text-xs text-danger-red">{errors.username.message}</p>
            )}
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label htmlFor="login-password" className="text-xs font-medium text-shell-muted">
                Password
              </label>
              <button
                type="button"
                onClick={() => setForgotPasswordOpen(true)}
                className="text-xs font-medium text-gold hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <div className={inputWrap}>
              <Icons.Lock className="shrink-0 text-shell-muted" size={13} />
              <input
                id="login-password"
                {...register('password')}
                onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                placeholder="Enter password"
                autoComplete="current-password"
                className={inputClass}
                type={showPassword ? 'text' : 'password'}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="shrink-0 text-shell-muted transition hover:text-shell-fg"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <Icons.EyeSlash size={16} /> : <Icons.Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-danger-red">{errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-gold font-semibold text-gold-foreground transition hover:bg-gold-hover disabled:opacity-60"
          >
            {isLoading ? (
              <span className="size-4 animate-spin rounded-full border-2 border-gold-foreground border-t-transparent" />
            ) : (
              <Icons.SignIn className="size-4" />
            )}
            Log in
          </button>

          <div className="flex items-center gap-3 text-xs text-shell-muted">
            <span className="h-px flex-1 bg-shell-border" />
            or
            <span className="h-px flex-1 bg-shell-border" />
          </div>

          <button
            type="button"
            onClick={handleDemoLogin}
            disabled={demoLoading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border border-shell-border font-semibold text-shell-fg transition hover:border-gold disabled:opacity-60"
          >
            {demoLoading && (
              <span className="size-4 animate-spin rounded-full border-2 border-gold border-t-transparent" />
            )}
            Try with Demo ID
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-shell-muted">
          New to {BRAND.name}?{' '}
          <button
            type="button"
            onClick={onSignup}
            className="font-semibold text-gold hover:underline"
          >
            Create an account
          </button>
        </p>
      </div>

      <ForgotPasswordModal open={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)} />
      <OTPModal
        data={verificationData}
        open={verification}
        onClose={() => setVerification(false)}
      />
    </div>
  );
}

export default LoginForm;
